import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Plus, AlertTriangle, Pencil } from "lucide-react";
import ExportImport from "@/components/ExportImport";
import { Dialog as RejDialog, DialogContent as RejContent, DialogHeader as RejHeader, DialogTitle as RejTitle } from "@/components/ui/dialog";

export default function Karyawan() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ role: "karyawan", salary_type: "monthly" });

  const load = async () => {
    const [u, s, sh] = await Promise.all([api.get("/users"), api.get("/sites"), api.get("/shifts")]);
    setUsers(u.data); setSites(s.data); setShifts(sh.data);
  };
  useEffect(() => { load(); }, []);

  const [rejUser, setRejUser] = useState(null);
  const [rejItems, setRejItems] = useState([]);
  const showRejections = async (u) => {
    setRejUser(u);
    try {
      const { data } = await api.get(`/absensi/rejections?user_id=${u.id}`);
      setRejItems(data);
    } catch { setRejItems([]); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const { password, email, ...rest } = form;
        // Strip empty strings — backend expects Optional[float]/Optional[str], not ""
        const payload = Object.fromEntries(
          Object.entries(rest).filter(([, v]) => v !== "" && v !== null && v !== undefined)
        );
        if (password) payload.password = password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Karyawan diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("Karyawan ditambahkan");
      }
      setForm({ role: "karyawan", salary_type: "monthly" });
      setEditId(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "karyawan",
      position: u.position || "",
      phone: u.phone || "",
      site_id: u.site_id || "",
      telegram_chat_id: u.telegram_chat_id || "",
      shift_id: u.shift_id || "",
      salary_type: u.salary_type || "monthly",
      basic_salary: u.basic_salary ?? "",
      hourly_rate: u.hourly_rate ?? "",
      daily_rate: u.daily_rate ?? "",
      password: "",
    });
    setOpen(true);
  };

  const startCreate = () => {
    setEditId(null);
    setForm({ role: "karyawan", salary_type: "monthly" });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus karyawan ini?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Manajemen Karyawan"
        subtitle="Kelola user, role, dan penempatan site"
        testId="karyawan-header"
        actions={
          <>
            <ExportImport module="users" canImport={true} />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm({ role: "karyawan", salary_type: "monthly" }); } }}>
            <DialogTrigger asChild>
              <Button data-testid="karyawan-add-btn" onClick={startCreate} className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">
                <Plus size={16} className="mr-1" /> Tambah Karyawan
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-2xl">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl">{editId ? "Edit Karyawan" : "Tambah Karyawan"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Nama</Label>
                  <Input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="karyawan-field-name" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Email</Label>
                  <Input required type="email" disabled={!!editId} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11 disabled:opacity-50" data-testid="karyawan-field-email" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Password {editId && <span className="text-neutral-600 normal-case">(kosongkan = tetap)</span>}</Label>
                  <Input required={!editId} type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="karyawan-field-password" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="karyawan-field-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="operasional">Operasional</SelectItem>
                      <SelectItem value="karyawan">Karyawan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Posisi</Label>
                  <Input value={form.position || ""} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Telepon</Label>
                  <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Site</Label>
                  <Select value={form.site_id || ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="karyawan-field-site"><SelectValue placeholder="Pilih site..." /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Telegram Chat ID</Label>
                  <Input value={form.telegram_chat_id || ""} onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })} placeholder="Opsional" className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Shift</Label>
                  <Select value={form.shift_id || ""} onValueChange={(v) => setForm({ ...form, shift_id: v })}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="karyawan-field-shift"><SelectValue placeholder="Pilih shift..." /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_hour}-{s.end_hour})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Tipe Gaji</Label>
                  <Select value={form.salary_type || "monthly"} onValueChange={(v) => setForm({ ...form, salary_type: v })}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="karyawan-field-salary-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      <SelectItem value="monthly">Bulanan</SelectItem>
                      <SelectItem value="daily">Harian</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Gaji Pokok (Rp)</Label>
                  <Input type="number" value={form.basic_salary ?? ""} onChange={(e) => setForm({ ...form, basic_salary: Number(e.target.value || 0) })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Upah / Jam (Rp)</Label>
                  <Input type="number" value={form.hourly_rate ?? ""} onChange={(e) => setForm({ ...form, hourly_rate: Number(e.target.value || 0) })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Upah / Hari (Rp)</Label>
                  <Input type="number" value={form.daily_rate ?? ""} onChange={(e) => setForm({ ...form, daily_rate: Number(e.target.value || 0) })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                  <Button type="submit" data-testid="karyawan-submit-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">Simpan</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </>
        }
      />
      <Section>
        {users.length === 0 ? <Empty /> : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Nama</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Email</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Role</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Site</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Shift</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Posisi</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Telepon</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-neutral-800" data-testid={`karyawan-row-${u.id}`}>
                    <TableCell className="font-mono">{u.name}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell><span className="text-xs font-mono uppercase px-2 py-1 border border-[#FF6600] text-[#FF6600]">{u.role}</span></TableCell>
                    <TableCell className="font-mono text-xs">{sites.find((s) => s.id === u.site_id)?.name || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{shifts.find((s) => s.id === u.shift_id)?.name || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{u.position || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{u.phone || "-"}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => startEdit(u)} title="Edit karyawan" data-testid={`karyawan-edit-${u.id}`} className="text-neutral-500 hover:text-[#FF6600] mr-3">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => showRejections(u)} title="Riwayat absen ditolak" data-testid={`karyawan-rejects-${u.id}`} className="text-neutral-500 hover:text-[#FFD700] mr-3">
                        <AlertTriangle size={14} />
                      </button>
                      <button onClick={() => remove(u.id)} className="text-neutral-500 hover:text-[#DC2626]" data-testid={`karyawan-delete-${u.id}`}><Trash2 size={14} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
      <RejDialog open={!!rejUser} onOpenChange={(o) => !o && setRejUser(null)}>
        <RejContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-2xl max-h-[80vh] overflow-y-auto">
          <RejHeader>
            <RejTitle className="font-display font-black text-2xl">Riwayat Absen Ditolak — {rejUser?.name}</RejTitle>
          </RejHeader>
          {rejItems.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Tidak ada absen ditolak — karyawan bersih ✓</div>
          ) : (
            <div className="space-y-2">
              <div className="font-mono text-[10px] uppercase text-[#DC2626] mb-2">{rejItems.length} penolakan total</div>
              {rejItems.map((r) => (
                <div key={r.id} className="border border-neutral-800 bg-[#141414] p-3" data-testid={`reject-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase px-2 py-0.5 border border-[#DC2626] text-[#DC2626]">{r.reason}</span>
                    <span className="font-mono text-xs text-neutral-500">{new Date(r.timestamp).toLocaleString("id-ID")}</span>
                  </div>
                  <div className="text-xs text-neutral-400 mt-2">{r.detail}</div>
                  <div className="font-mono text-[10px] text-neutral-600 mt-1">GPS: {r.lat?.toFixed(5)}, {r.lng?.toFixed(5)} · Type: {r.type}</div>
                </div>
              ))}
            </div>
          )}
        </RejContent>
      </RejDialog>
    </div>
  );
}
